import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { XCircle, Loader2, Package, Truck } from "lucide-react";

const KIND_STYLE = {
  parcel: "bg-slate-100 text-slate-700",
  trip: "bg-indigo-100 text-indigo-700",
};

const ROLE_STYLE = {
  sender: "bg-amber-100 text-amber-800",
  carrier: "bg-blue-100 text-blue-700",
};

const Cancellations = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ kind: "all", actor_role: "all", q: "", date_from: "", date_to: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (filters.kind !== "all") params.kind = filters.kind;
      if (filters.actor_role !== "all") params.actor_role = filters.actor_role;
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await adminApi.get("/cancellations", { params });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / 50)), [total]);

  return (
    <div className="p-6" data-testid="admin-cancellations-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><XCircle className="w-5 h-5 text-rose-600"/> Cancellations</h1>
          <p className="text-xs text-slate-500 mt-1">Every trip + parcel cancellation, newest first. Filter by kind, actor role, reason, or date range.</p>
        </div>
        <div className="text-xs text-slate-500">Total: <b>{total}</b></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
        <Select value={filters.kind} onValueChange={(v) => setFilters(f => ({ ...f, kind: v }))}>
          <SelectTrigger data-testid="filter-kind"><SelectValue placeholder="Kind"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="parcel">Parcel</SelectItem>
            <SelectItem value="trip">Trip</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.actor_role} onValueChange={(v) => setFilters(f => ({ ...f, actor_role: v }))}>
          <SelectTrigger data-testid="filter-role"><SelectValue placeholder="Actor"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actors</SelectItem>
            <SelectItem value="sender">Senders</SelectItem>
            <SelectItem value="carrier">Carriers</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid="filter-q"
          placeholder="Search reason"
          value={filters.q}
          onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
        />
        <Input type="date" data-testid="filter-from" value={filters.date_from}
               onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}/>
        <div className="flex gap-2">
          <Input type="date" data-testid="filter-to" value={filters.date_to}
                 onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}/>
          <Button onClick={() => { setPage(1); load(); }} data-testid="apply-filters">Apply</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Kind</th>
              <th className="text-left px-4 py-2">Actor</th>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-right px-4 py-2">Refund</th>
              <th className="text-right px-4 py-2">Affected</th>
              <th className="text-left px-4 py-2">Entity</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin inline-block"/> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No cancellations in this window.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cancel-row-${r.id}`}>
                <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-2">
                  <Badge className={KIND_STYLE[r.kind] || "bg-slate-100"}>
                    {r.kind === "trip" ? <><Truck className="w-3 h-3 inline mr-1"/>Trip</> : <><Package className="w-3 h-3 inline mr-1"/>Parcel</>}
                  </Badge>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Badge className={ROLE_STYLE[r.actor_role] || "bg-slate-100"}>{r.actor_role}</Badge>
                    <Link
                      to={`/admin/users/${r.actor_id}`}
                      className="text-xs text-slate-700 hover:underline"
                      data-testid={`cancel-actor-${r.id}`}
                    >{r.actor?.name || r.actor_id?.slice(0, 8)} · {r.actor?.phone || ""}</Link>
                  </div>
                </td>
                <td className="px-4 py-2 text-xs text-slate-700 max-w-md">{r.reason}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {r.refund_amount > 0 ? `₹${r.refund_amount.toFixed(0)}` : "—"}
                </td>
                <td className="px-4 py-2 text-right text-xs">
                  {r.kind === "trip"
                    ? `${(r.affected_parcel_ids || []).length} parcel${(r.affected_parcel_ids || []).length !== 1 ? "s" : ""}`
                    : `${(r.affected_delivery_ids || []).length} delivery`}
                </td>
                <td className="px-4 py-2 text-xs">
                  {r.kind === "parcel" ? (
                    <Link to={`/admin/parcels/${r.entity_id}`} className="text-slate-900 underline">Open parcel</Link>
                  ) : (
                    <span className="text-slate-400 font-mono">{r.entity_id.slice(0, 8)}…</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs">
          <div className="text-slate-500">Page {page} of {pageCount}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} data-testid="page-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cancellations;
